import { useRef } from "react";
import { hsvToRgbUnit, unitToRgb8, rgb8ToHex, type Hsv } from "@hunch/core";
import styles from "./ColorPickerEngine.module.css";

export interface ColorPickerEngineProps {
  value: Hsv;
  onChange: (value: Hsv) => void;
  disabled?: boolean;
  hueAriaLabel: string;
  areaAriaLabel: string;
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

/**
 * Hue slider + 4:3 saturation/value area, per spec §9.3. Reusable: no
 * numeric/hex input, pointer-capture driven, 2%/10%(shift) arrow-key steps
 * on the area. The chosen color is quantized to 8-bit sRGB by the caller
 * via the same hsvToRgbUnit/unitToRgb8 path used here for the live preview,
 * so what's shown is exactly what's scored.
 */
export function ColorPickerEngine({
  value,
  onChange,
  disabled = false,
  hueAriaLabel,
  areaAriaLabel,
}: ColorPickerEngineProps) {
  const areaRef = useRef<HTMLDivElement>(null);

  function setFromPointer(clientX: number, clientY: number) {
    const rect = areaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const s = clamp01((clientX - rect.left) / rect.width);
    const v = clamp01(1 - (clientY - rect.top) / rect.height);
    onChange({ ...value, s, v });
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setFromPointer(e.clientX, e.clientY);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (disabled || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    setFromPointer(e.clientX, e.clientY);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    const step = e.shiftKey ? 0.1 : 0.02;
    let { s, v } = value;
    if (e.key === "ArrowLeft") s = clamp01(s - step);
    else if (e.key === "ArrowRight") s = clamp01(s + step);
    else if (e.key === "ArrowUp") v = clamp01(v + step);
    else if (e.key === "ArrowDown") v = clamp01(v - step);
    else return;
    e.preventDefault();
    onChange({ ...value, s, v });
  }

  const hueColorHex = rgb8ToHex(unitToRgb8(hsvToRgbUnit({ h: value.h, s: 1, v: 1 })));

  return (
    <div className={styles.wrapper}>
      <div
        ref={areaRef}
        className={styles.area}
        tabIndex={disabled ? -1 : 0}
        role="group"
        aria-label={areaAriaLabel}
        aria-disabled={disabled}
        style={{ backgroundColor: hueColorHex }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.whiteLayer} />
        <div className={styles.blackLayer} />
        <div
          className={styles.handle}
          style={{ left: `${value.s * 100}%`, top: `${(1 - value.v) * 100}%` }}
        />
      </div>

      <input
        type="range"
        className={styles.hueSlider}
        min={0}
        max={360}
        step={1}
        value={value.h}
        disabled={disabled}
        aria-label={hueAriaLabel}
        onChange={(e) => onChange({ ...value, h: Number(e.target.value) })}
      />
    </div>
  );
}
